import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  getIntegrationLogs,
  IntegrationLog,
  IntegrationName,
} from '@/lib/integrations-service';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  History,
  Calendar,
  CreditCard,
  Mail,
  Activity,
  RefreshCw,
  FileText,
  User,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

const statusConfig = {
  success: { 
    icon: CheckCircle, 
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    dotColor: 'bg-emerald-500'
  },
  warning: { 
    icon: AlertTriangle, 
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    dotColor: 'bg-amber-500'
  },
  error: { 
    icon: XCircle, 
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    dotColor: 'bg-red-500'
  },
};

const integrationConfig: Record<IntegrationName, { icon: typeof Calendar; label: string; color: string }> = {
  google_calendar: { icon: Calendar, label: 'Google Calendar', color: 'text-blue-500' },
  calendly: { icon: Calendar, label: 'Calendly', color: 'text-blue-600' },
  stripe: { icon: CreditCard, label: 'Stripe', color: 'text-violet-500' },
  email: { icon: Mail, label: 'Email', color: 'text-rose-500' },
  system_health: { icon: Activity, label: 'System Health', color: 'text-emerald-500' },
};

interface IntegrationLogsTableProps {
  defaultFilter?: IntegrationName | 'all';
}

const IntegrationLogsTable = ({ defaultFilter = 'all' }: IntegrationLogsTableProps) => {
  const [logs, setLogs] = useState<IntegrationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<IntegrationName | 'all'>(defaultFilter);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const result = await getIntegrationLogs({
        integrationName: filter,
        page,
        pageSize,
      });
      setLogs(result.logs);
      setTotal(result.total);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filter, page]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <History className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Integration Logs</CardTitle>
              <p className="text-sm text-muted-foreground">Test history and results</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={filter}
              onValueChange={(value) => {
                setFilter(value as IntegrationName | 'all');
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    All Integrations
                  </div>
                </SelectItem>
                <SelectItem value="google_calendar">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    Google Calendar
                  </div>
                </SelectItem>
                <SelectItem value="stripe">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-violet-500" />
                    Stripe
                  </div>
                </SelectItem>
                <SelectItem value="email">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-rose-500" />
                    Email
                  </div>
                </SelectItem>
                <SelectItem value="system_health">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-emerald-500" />
                    System Health
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={fetchLogs} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-64" />
                </div>
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <History className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold mb-1">No logs found</h3>
            <p className="text-sm text-muted-foreground">Run integration tests to generate logs</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[180px]">Date/Time</TableHead>
                    <TableHead className="w-[160px]">Integration</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <AnimatePresence mode="popLayout">
                    {logs.map((log, index) => {
                      const StatusIcon = statusConfig[log.status].icon;
                      const integration = integrationConfig[log.integration_name];

                      return (
                        <motion.tr
                          key={log.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ delay: index * 0.03 }}
                          className="border-b"
                        >
                          <TableCell>
                            <div className="space-y-1">
                              <p className="text-sm font-medium">
                                {format(new Date(log.created_at), 'MMM d, yyyy')}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(log.created_at), 'HH:mm:ss')}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <integration.icon className={`h-4 w-4 ${integration.color}`} />
                              <span className="text-sm font-medium">{integration.label}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${statusConfig[log.status].className} gap-1`}>
                              <StatusIcon className="h-3 w-3" />
                              {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[300px]">
                            <p className="text-sm break-words line-clamp-2" title={log.message}>
                              {log.message}
                            </p>
                          </TableCell>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              <AnimatePresence mode="popLayout">
                {logs.map((log, index) => {
                  const StatusIcon = statusConfig[log.status].icon;
                  const integration = integrationConfig[log.integration_name];

                  return (
                    <motion.div
                      key={log.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ delay: index * 0.03 }}
                      className="p-4 rounded-lg border bg-card"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <integration.icon className={`h-4 w-4 ${integration.color}`} />
                          <span className="font-medium text-sm">{integration.label}</span>
                        </div>
                        <Badge className={`${statusConfig[log.status].className} gap-1 text-xs`}>
                          <StatusIcon className="h-3 w-3" />
                          {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2 break-words line-clamp-3">{log.message}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                      </p>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm px-2 min-w-[80px] text-center">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default IntegrationLogsTable;
